import { ANVIL_ACCOUNTS } from "@gotts.ai/testnet";
import { encodeFunctionData, keccak256, toHex } from "viem";

import { AGENT_VAULT_FACTORY_ABI } from "../contracts/vault/agent-vault-factory";

import type { DeployContext } from "../deployer/deploy-utils";
import type { MockTokenAddresses, VaultAddresses } from "../deployer/types";
import type { Address } from "viem";

export interface CreatedVault {
  /** Human-readable vault name */
  name: string;
  /** Vault proxy address */
  vault: Address;
  /** Creator wallet address */
  creator: Address;
  /** ERC-8004 agent ID of the creator */
  agentId: number;
  /** Base asset address */
  baseAsset: Address;
  /** Management fee in bps */
  managementFeeBps: number;
  /** Performance fee in bps */
  performanceFeeBps: number;
  /** Risk rating (1-10) */
  riskRating: number;
}

export interface SeedVaultsResult {
  vaults: CreatedVault[];
  durationMs: number;
}

/**
 * Seed vault definitions for the devenv test environment.
 *
 * Each vault is created by the agent wallet corresponding to the agentId
 * registered in the seedAgents step.
 */
const VAULT_DEFS = [
  {
    name: "USDC Yield Vault",
    accountIndex: 1, // alice
    agentId: 1,
    baseAssetKey: "usdc" as const,
    managementFeeBps: 200,
    performanceFeeBps: 2000,
    riskRating: 3,
    strategy:
      "Conservative USDC yield farming via Uniswap V3 concentrated liquidity",
  },
  {
    name: "WETH Growth Vault",
    accountIndex: 2, // bob
    agentId: 2,
    baseAssetKey: "weth" as const,
    managementFeeBps: 300,
    performanceFeeBps: 3000,
    riskRating: 6,
    strategy: "Aggressive WETH growth strategy via multi-pool arbitrage",
  },
  {
    name: "USDC Multi-Asset Vault",
    accountIndex: 3, // carol
    agentId: 3,
    baseAssetKey: "usdc" as const,
    managementFeeBps: 100,
    performanceFeeBps: 1500,
    riskRating: 2,
    strategy: "Diversified USDC vault across multiple Uniswap pools",
  },
] as const;

/**
 * Create 3 seed vaults via the AgentVaultFactory.
 *
 * Each vault is created by impersonating the corresponding agent wallet
 * (since on Anvil we can send transactions from any account). The factory's
 * `createVault` function deploys a new ERC-4626 vault clone.
 */
export async function seedVaults(
  ctx: DeployContext,
  vaultAddresses: VaultAddresses,
  tokens: MockTokenAddresses,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeedVaultsResult> {
  const start = Date.now();
  const vaults: CreatedVault[] = [];

  for (let i = 0; i < VAULT_DEFS.length; i++) {
    const def = VAULT_DEFS[i];
    if (!def) continue;
    const account = ANVIL_ACCOUNTS[def.accountIndex];
    if (!account) {
      throw new Error(
        `Anvil account at index ${String(def.accountIndex)} not found`,
      );
    }

    const baseAsset = tokens[def.baseAssetKey];

    await onAction?.(
      `Creating vault ${String(i + 1)}/${String(VAULT_DEFS.length)}: ${def.name}`,
    );

    // Impersonate the agent wallet
    await ctx.request({
      method: "anvil_impersonateAccount",
      params: [account.address],
    });

    const strategyHash = keccak256(toHex(def.strategy));

    const createVaultData = encodeFunctionData({
      abi: AGENT_VAULT_FACTORY_ABI,
      functionName: "createVault",
      args: [
        {
          baseAsset,
          creatorAgentId: BigInt(def.agentId),
          managementFeeBps: def.managementFeeBps,
          performanceFeeBps: def.performanceFeeBps,
          identityGated: true,
          metadataURI: `https://gotts.ai/vaults/${def.name.toLowerCase().replace(/ /g, "-")}`,
          disclosure: {
            strategyDescription: def.strategy,
            strategyHash,
            riskRating: def.riskRating,
            disclaimerURI: "https://gotts.ai/disclaimer",
          },
        },
      ],
    });

    const txHash = await ctx.walletClient.sendTransaction({
      account: account.address,
      to: vaultAddresses.vaultFactory,
      data: createVaultData,
      chain: ctx.walletClient.chain,
    });

    const receipt = await ctx.publicClient.waitForTransactionReceipt({
      hash: txHash,
    });

    // Parse VaultCreated event to get the vault address
    // VaultCreated(address indexed vault, uint256 indexed creatorAgentId, ...)
    const vaultCreatedTopic =
      "0xfd6f4ff90d9fc4893ad75a6ae3638617ffa1ac2538ffb704db56dc77bb4a4b1a";
    const vaultLog = receipt.logs.find(
      (log) => log.topics[0] === vaultCreatedTopic,
    );

    let vaultAddress: Address;
    if (vaultLog?.topics[1]) {
      // The vault address is the first indexed parameter (topic[1])
      vaultAddress = `0x${vaultLog.topics[1].slice(26)}`;
    } else {
      throw new Error(
        `VaultCreated event not found in receipt for ${def.name}`,
      );
    }

    // Stop impersonating
    await ctx.request({
      method: "anvil_stopImpersonatingAccount",
      params: [account.address],
    });

    vaults.push({
      name: def.name,
      vault: vaultAddress,
      creator: account.address,
      agentId: def.agentId,
      baseAsset,
      managementFeeBps: def.managementFeeBps,
      performanceFeeBps: def.performanceFeeBps,
      riskRating: def.riskRating,
    });
  }

  return {
    vaults,
    durationMs: Date.now() - start,
  };
}
